import React, { useEffect, useRef, useState } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import { containerAPI } from '@hpanel/web-common/api/client'

export const ConsolePanel: React.FC<{ containerId: number }> = ({ containerId }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [closed, setClosed] = useState<string | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 13,
      theme: { background: '#111' },
    })
    const fit = new FitAddon()
    term.loadAddon(fit)
    term.open(containerRef.current)
    fit.fit()

    const url = containerAPI.consoleUrl(containerId, term.cols, term.rows)
    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch (err) {
      console.error('[ConsolePanel] failed to construct WebSocket', err)
      setError('Unable to reach console')
      return
    }
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => { term.focus() }
    ws.onmessage = (ev) => {
      if (ev.data instanceof ArrayBuffer) {
        term.write(new Uint8Array(ev.data))
      } else if (typeof ev.data === 'string') {
        term.write(ev.data)
      }
    }
    ws.onerror = (ev) => {
      console.error('[ConsolePanel] WS error', ev)
      setError('Unable to reach console')
    }
    ws.onclose = (ev) => {
      console.error('[ConsolePanel] WS closed code=', ev.code, 'reason=', ev.reason)
      const map: Record<number, string> = {
        4401: 'Session ended — not authenticated',
        4403: 'Session ended — not authorised',
        4001: 'Replaced by newer session',
        4408: 'Session ended — idle timeout',
        4413: 'Session ended — 4h cap reached',
        1011: 'Session ended — backend unavailable',
        1000: 'Session ended',
      }
      setClosed(map[ev.code] ?? `Session ended (code ${ev.code})`)
    }

    const onData = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(new TextEncoder().encode(data))
    })

    const sendResize = () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }))
      }
    }

    const onResize = () => { fit.fit(); sendResize() }
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      onData.dispose()
      ws.close()
      term.dispose()
    }
  }, [containerId])

  return (
    <div data-testid="console-panel" className="space-y-2">
      {error && <div data-testid="console-error" className="text-red-700">{error}</div>}
      {closed && <div data-testid="console-closed" className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{closed}</div>}
      <div ref={containerRef} data-testid="console-terminal" className="h-[60vh] bg-black rounded" />
    </div>
  )
}
